/**
 * PRODUCTION-INTENT: Presidio home content contract.
 * Swap this static provider for CMS/dotCMS/ICM without changing the home page.
 *
 * Dashboard metrics / recent orders / attention items below include DEMO values
 * for the prototype until live account aggregates are wired.
 */

export interface PresidioHomeCategoryTile {
  categoryId: string;
  titleKey: string;
  bodyKey: string;
  /** Brand Guidelines lined icon asset under /assets/themes/presidio/img/icons/ */
  iconSrc: string;
}

export interface PresidioHomeMetric {
  titleKey: string;
  valueKey: string;
  hintKey: string;
  link: string;
  /** Brand Guidelines lined icon asset under /assets/themes/presidio/img/icons/ */
  iconSrc: string;
  testingId: string;
}

export interface PresidioHomeOrderRow {
  id: string;
  placedKey: string;
  items: number;
  statusKey: string;
  /** shipping | processing | delivered */
  statusTone: 'shipping' | 'processing' | 'delivered';
  totalKey: string;
}

export interface PresidioHomeAttentionItem {
  titleKey: string;
  bodyKey: string;
  ctaKey: string;
  link: string;
  tone: 'invoice' | 'quote' | 'info';
  iconSrc: string;
}

export interface PresidioHomeServiceCard {
  eyebrowKey: string;
  titleKey: string;
  bodyKey: string;
  ctaKey: string;
  link: string;
  /** procurement photo | advisor badge | guidance photo */
  visual: 'procurement' | 'advisor' | 'guidance';
  /** Optional brand lined icon (advisor card) */
  iconSrc?: string;
}

export interface PresidioHomeContent {
  hero: {
    eyebrowKey: string;
    titleKey: string;
    leadKey: string;
    primaryCtaKey: string;
    /** Prefer catalog root; falls back to a known category. */
    primaryLink: string;
    primaryCategoryId?: string;
  };
  categories: {
    titleKey: string;
    leadKey: string;
    browseAllKey: string;
    browseAllLink: string;
    tiles: PresidioHomeCategoryTile[];
  };
  metrics: {
    ariaKey: string;
    items: PresidioHomeMetric[];
  };
  recentOrders: {
    titleKey: string;
    viewAllKey: string;
    viewAllLink: string;
    emptyKey: string;
    columns: {
      orderKey: string;
      placedKey: string;
      itemsKey: string;
      statusKey: string;
      totalKey: string;
    };
    /** DEMO: representative order rows for home dashboard */
    rows: PresidioHomeOrderRow[];
  };
  attention: {
    titleKey: string;
    items: PresidioHomeAttentionItem[];
  };
  services: {
    titleKey: string;
    leadKey: string;
    cards: PresidioHomeServiceCard[];
  };
}

export const PRESIDIO_HOME_CONTENT: PresidioHomeContent = {
  hero: {
    eyebrowKey: 'home.hero.eyebrow',
    titleKey: 'home.hero.title',
    leadKey: 'home.hero.lead',
    primaryCtaKey: 'home.hero.cta_primary',
    // DIAS category URLs are `/c/{uniqueId}` (see generateCategoryUrl)
    primaryLink: '/c/SHOP',
    // Catalog root CLP — all shopping top-level categories (not a single department like HW)
    primaryCategoryId: 'SHOP',
  },
  categories: {
    titleKey: 'home.categories.title',
    leadKey: 'home.categories.lead',
    browseAllKey: 'home.categories.browse_all',
    browseAllLink: '/c/SHOP',
    tiles: [
      {
        categoryId: 'HW',
        titleKey: 'home.categories.computers.title',
        bodyKey: 'home.categories.computers.body',
        iconSrc: '/assets/themes/presidio/img/icons/icon-systems.svg',
      },
      {
        categoryId: 'NET',
        titleKey: 'home.categories.networking.title',
        bodyKey: 'home.categories.networking.body',
        iconSrc: '/assets/themes/presidio/img/icons/pillar-cybersecurity.svg',
      },
      {
        categoryId: 'CLOUD',
        titleKey: 'home.categories.cloud.title',
        bodyKey: 'home.categories.cloud.body',
        iconSrc: '/assets/themes/presidio/img/icons/pillar-cloud.svg',
      },
      {
        categoryId: 'OFFICE',
        titleKey: 'home.categories.office.title',
        bodyKey: 'home.categories.office.body',
        iconSrc: '/assets/themes/presidio/img/icons/icon-application-stack.svg',
      },
      {
        categoryId: 'WH',
        titleKey: 'home.categories.storage.title',
        bodyKey: 'home.categories.storage.body',
        iconSrc: '/assets/themes/presidio/img/icons/icon-datacenter.svg',
      },
      {
        categoryId: 'AV',
        titleKey: 'home.categories.power.title',
        bodyKey: 'home.categories.power.body',
        iconSrc: '/assets/themes/presidio/img/icons/icon-collaboration.svg',
      },
    ],
  },
  metrics: {
    ariaKey: 'home.metrics.aria',
    items: [
      {
        titleKey: 'home.metrics.orders.title',
        valueKey: 'home.metrics.orders.value',
        hintKey: 'home.metrics.orders.hint',
        link: '/account/orders',
        iconSrc: '/assets/themes/presidio/img/icons/icon-servers.svg',
        testingId: 'orders',
      },
      {
        titleKey: 'home.metrics.quotes.title',
        valueKey: 'home.metrics.quotes.value',
        hintKey: 'home.metrics.quotes.hint',
        link: '/account/quotes',
        iconSrc: '/assets/themes/presidio/img/icons/icon-email.svg',
        testingId: 'quotes',
      },
      {
        titleKey: 'home.metrics.invoices.title',
        valueKey: 'home.metrics.invoices.value',
        hintKey: 'home.metrics.invoices.hint',
        link: '/account/invoices',
        iconSrc: '/assets/themes/presidio/img/icons/icon-folder.svg',
        testingId: 'invoices',
      },
      {
        titleKey: 'home.metrics.contracts.title',
        valueKey: 'home.metrics.contracts.value',
        hintKey: 'home.metrics.contracts.hint',
        link: '/account/contracts',
        iconSrc: '/assets/themes/presidio/img/icons/pillar-lifecycle-services.svg',
        testingId: 'contracts',
      },
    ],
  },
  recentOrders: {
    titleKey: 'home.orders.title',
    viewAllKey: 'home.orders.view_all',
    viewAllLink: '/account/orders',
    emptyKey: 'home.orders_empty',
    columns: {
      orderKey: 'home.orders.col.order',
      placedKey: 'home.orders.col.placed',
      itemsKey: 'home.orders.col.items',
      statusKey: 'home.orders.col.status',
      totalKey: 'home.orders.col.total',
    },
    rows: [
      {
        id: 'MP-104582',
        placedKey: 'home.orders.demo.row1.placed',
        items: 12,
        statusKey: 'home.orders.status.shipping',
        statusTone: 'shipping',
        totalKey: 'home.orders.demo.row1.total',
      },
      {
        id: 'MP-104501',
        placedKey: 'home.orders.demo.row2.placed',
        items: 3,
        statusKey: 'home.orders.status.processing',
        statusTone: 'processing',
        totalKey: 'home.orders.demo.row2.total',
      },
      {
        id: 'MP-104418',
        placedKey: 'home.orders.demo.row3.placed',
        items: 7,
        statusKey: 'home.orders.status.delivered',
        statusTone: 'delivered',
        totalKey: 'home.orders.demo.row3.total',
      },
      {
        id: 'MP-104390',
        placedKey: 'home.orders.demo.row4.placed',
        items: 1,
        statusKey: 'home.orders.status.delivered',
        statusTone: 'delivered',
        totalKey: 'home.orders.demo.row4.total',
      },
    ],
  },
  attention: {
    titleKey: 'home.attention.title',
    items: [
      {
        titleKey: 'home.attention.invoice.title',
        bodyKey: 'home.attention.invoice.body',
        ctaKey: 'home.attention.invoice.cta',
        link: '/account/invoices',
        tone: 'invoice',
        iconSrc: '/assets/themes/presidio/img/icons/icon-folder.svg',
      },
      {
        titleKey: 'home.attention.quote.title',
        bodyKey: 'home.attention.quote.body',
        ctaKey: 'home.attention.quote.cta',
        link: '/account/quotes',
        tone: 'quote',
        iconSrc: '/assets/themes/presidio/img/icons/icon-email.svg',
      },
    ],
  },
  services: {
    titleKey: 'home.services.title',
    leadKey: 'home.services.lead',
    cards: [
      {
        eyebrowKey: 'home.services.procurement.eyebrow',
        titleKey: 'home.services.procurement.title',
        bodyKey: 'home.services.procurement.body',
        ctaKey: 'home.services.procurement.cta',
        link: '/account',
        visual: 'procurement',
        iconSrc: '/assets/themes/presidio/img/icons/pillar-lifecycle-services.svg',
      },
      {
        eyebrowKey: 'home.services.advisor.eyebrow',
        titleKey: 'home.services.advisor.title',
        bodyKey: 'home.services.advisor.body',
        ctaKey: 'home.services.advisor.cta',
        link: '/account/devices',
        visual: 'advisor',
        iconSrc: '/assets/themes/presidio/img/icons/pillar-cloud.svg',
      },
      {
        eyebrowKey: 'home.services.guidance.eyebrow',
        titleKey: 'home.services.guidance.title',
        bodyKey: 'home.services.guidance.body',
        ctaKey: 'home.services.guidance.cta',
        link: '/account',
        visual: 'guidance',
      },
    ],
  },
};
